/* eslint-disable @typescript-eslint/no-explicit-any */
import { useContext } from 'react';
import { SearchResultAlertContext } from '../../organisms/OSearchResults/index.context';
import {
  useSearchResultAlertZustandStore,
  SearchResultAlertContextType,
} from '../../modules/store/searchResultAlertZustandStore';
import { useClientEnvVarsStore } from '@marriott/mi-store-utils';
import { FEATURE_FLAGS, FLAG_TYPE } from '@marriott/mi-feature-flag-utils';
import { SearchResultAlertType } from '../../organisms/OSearchResults/index.types';

interface UseConditionalSearchResultAlertContextOptions {
  /**
   * Specify which component is using this hook to determine the correct feature flag
   * 'adf' will check EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND (for AvailabilityCalendar and CalendarWrapper)
   * 'serp' will check EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND (for all other components)
   */
  flagType?: 'adf' | 'serp';
}

/**
 * Hook that conditionally returns data from either the Zustand store or React Context
 * based on feature flags, maintaining the same API surface for seamless migration
 */
export const useConditionalSearchResultAlertContext = (
  options: UseConditionalSearchResultAlertContextOptions = { flagType: 'serp' }
): SearchResultAlertContextType => {
  const { flagType = 'serp' } = options;

  // Get feature flags from the client environment store
  const featureFlags = useClientEnvVarsStore(state => state.envVarsObject['featureFlags']) as unknown as Record<
    string,
    FLAG_TYPE
  >;

  // Determine which feature flag to check based on component type
  const shouldUseZustand =
    flagType === 'adf'
      ? featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND] ?? false
      : featureFlags?.[FEATURE_FLAGS.EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND] ?? false;

  // Get data from React Context (fallback)
  const contextData = useContext(SearchResultAlertContext);

  // Get Zustand store functions and state
  const zustandStore = useSearchResultAlertZustandStore();
  const { setSearchResultAlerts: zustandSetSearchResultAlerts, ...zustandState } = zustandStore;

  if (shouldUseZustand) {
    // Return Zustand store data with methods that match SearchResultAlertContext API
    return {
      searchResultAlerts: zustandState.searchResultAlerts,
      setSearchResultAlerts: zustandSetSearchResultAlerts,
    };
  } else {
    // Return React Context data
    return {
      searchResultAlerts: contextData.searchResultAlerts,
      setSearchResultAlerts: contextData.setSearchResultAlerts,
    };
  }
};

/**
 * Convenience hook specifically for ADF components (AvailabilityCalendar, CalendarWrapper)
 * that checks EXPERIENCE_MCOM_SHOP_ADF_ZUSTAND flag
 */
export const useConditionalSearchResultAlertContextADF = () => {
  return useConditionalSearchResultAlertContext({ flagType: 'adf' });
};

/**
 * Convenience hook specifically for SERP components
 * that checks EXPERIENCE_MCOM_SHOP_SERP_ZUSTAND flag
 */
export const useConditionalSearchResultAlertContextSERP = () => {
  return useConditionalSearchResultAlertContext({ flagType: 'serp' });
};
